<template>
  <v-app>
    <Suspense>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </Suspense>
  </v-app>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import type { RouteMeta } from './router/routes'

const route = useRoute()

const meta = computed(() => route.meta as RouteMeta | undefined)

// Default meta values
const DEFAULT_TITLE = 'Jasmine Puozzo Wellness Centre | Massage & Beauty'
const DEFAULT_DESCRIPTION = 'Experience ultimate relaxation at Jasmine Puozzo Wellness Centre. Professional massage therapy, beauty treatments, and wellness services.'
const DEFAULT_IMAGE = '/img/opengraph/home.webp'
const SITE_URL = 'https://jasminewellnesscentre.ie'

useHead({
  title: computed(() => meta.value?.title || DEFAULT_TITLE),
  meta: [
    {
      name: 'description',
      content: computed(() => meta.value?.description || DEFAULT_DESCRIPTION),
    },
    {
      name: 'keywords',
      content: computed(() => meta.value?.keywords || ''),
    },
    // Open Graph
    {
      property: 'og:title',
      content: computed(() => meta.value?.title || DEFAULT_TITLE),
    },
    {
      property: 'og:description',
      content: computed(() => meta.value?.description || DEFAULT_DESCRIPTION),
    },
    {
      property: 'og:image',
      content: computed(() => meta.value?.ogImage || DEFAULT_IMAGE),
    },
    {
      property: 'og:image:width',
      content: '1200',
    },
    {
      property: 'og:image:height',
      content: '630',
    },
    {
      property: 'og:url',
      content: computed(() => `${SITE_URL}${route.path}`),
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:site_name',
      content: 'Jasmine Puozzo Wellness Centre',
    },
    // Twitter Card
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: computed(() => meta.value?.title || DEFAULT_TITLE),
    },
    {
      name: 'twitter:description',
      content: computed(() => meta.value?.description || DEFAULT_DESCRIPTION),
    },
    {
      name: 'twitter:image',
      content: computed(() => meta.value?.ogImage || DEFAULT_IMAGE),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: computed(() => `${SITE_URL}${route.path === '/' ? '' : route.path}`),
    },
  ],
})
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Global styles for better mobile experience */
@media (max-width: 600px) {
  .v-btn {
    letter-spacing: normal !important;
  }
  
  .text-h4 {
    font-size: 1.75rem !important;
  }
  
  .text-h5 {
    font-size: 1.5rem !important;
  }
  
  .text-h6 {
    font-size: 1.25rem !important;
  }
  
  .text-body-1 {
    font-size: 1rem !important;
  }
  
  .text-body-2 {
    font-size: 0.875rem !important;
  }
  
  .content {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}

/* Improved scrollbar styles */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

::-webkit-scrollbar-track {
  background: rgba(4, 35, 26, 0.6);
}

::-webkit-scrollbar-thumb {
  background: rgba(232, 220, 197, 0.6);
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(232, 220, 197, 0.8);
}
</style>